import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MailCheck, AlertCircle } from 'lucide-react';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Field, Input } from '../ui/Field.js';
import { useToast } from '../ui/index.js';
import { apiClient, ApiError } from '../../utils/apiClient.js';
import { useAuthStore } from '../../stores/authStore.js';

const CODE_LENGTH = 6;

/** Farsi/Arabic-Indic digits typed on a phone keyboard -> latin digits, other characters dropped. */
const normalizeCode = (raw: string): string =>
  raw
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, '')
    .slice(0, CODE_LENGTH);

interface VerifyResponse {
  user: { email: string; email_verified: boolean };
  token: string;
}

/** FRE-137: enter the 6-digit code emailed at registration. Global; opened via `useAuthStore.openVerifyDialog()`. */
export const VerifyEmailDialog: React.FC = () => {
  const toast = useToast();
  const open = useAuthStore((s) => s.verifyDialogOpen);
  const email = useAuthStore((s) => s.user?.email);
  const closeVerifyDialog = useAuthStore((s) => s.closeVerifyDialog);
  const applyVerifiedUser = useAuthStore((s) => s.applyVerifiedUser);
  const pendingEmail = useAuthStore((s) => s.pendingEmail);
  const openChangeEmailDialog = useAuthStore((s) => s.openChangeEmailDialog);
  const isChange = Boolean(pendingEmail);
  const sentTo = pendingEmail ?? email;

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    if (!open) {
      setCode('');
      setError(null);
      setBusy(false);
      setCooldown(0);
    }
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const submit = useCallback(
    async (value: string) => {
      if (submitting.current) return;
      if (value.length !== CODE_LENGTH) {
        setError('کد تایید ۶ رقم است.');
        return;
      }
      submitting.current = true;
      setBusy(true);
      setError(null);
      try {
        const res = await apiClient.post<VerifyResponse>('/api/auth/verify-email', { code: value });
        applyVerifiedUser({ email: res.user.email, emailVerified: res.user.email_verified }, res.token);
        toast.success(isChange ? 'ایمیل جدید شما ثبت و تایید شد.' : 'ایمیل شما تایید شد.');
        closeVerifyDialog();
      } catch (err: unknown) {
        if (err instanceof ApiError && err.data.code === 'CODE_INVALID') {
          setError('کد واردشده درست نیست.');
        } else if (err instanceof ApiError && err.data.code === 'EMAIL_TAKEN') {
          setError('این ایمیل در این فاصله توسط حساب دیگری ثبت شد. ایمیل دیگری انتخاب کنید.');
        } else if (err instanceof ApiError && err.status === 410) {
          setError('کد منقضی شده است. کد جدید بگیرید.');
        } else if (err instanceof ApiError && err.status === 429) {
          setError('تلاش‌های زیاد؛ کمی بعد دوباره امتحان کنید.');
        } else {
          setError('تایید انجام نشد؛ اتصال اینترنت را بررسی کنید.');
        }
        setCode('');
      } finally {
        submitting.current = false;
        setBusy(false);
      }
    },
    [closeVerifyDialog, applyVerifiedUser, isChange, toast]
  );

  const resend = async () => {
    setError(null);
    try {
      await apiClient.post('/api/auth/resend-verification', {});
      toast.success('کد جدید به ایمیل شما ارسال شد.');
      setCooldown(60);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) {
        setCooldown(Math.max(1, Math.ceil(err.data.retryAfter ?? 60)));
      } else {
        setError('ارسال کد انجام نشد؛ اتصال اینترنت را بررسی کنید.');
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeVerifyDialog}
      title={isChange ? 'تایید ایمیل جدید' : 'تایید ایمیل'}
      titleHeadingLevel="h3"
      description={sentTo ? `کد ۶ رقمی ارسال‌شده به ${sentTo} را وارد کنید.` : 'کد ۶ رقمی ارسال‌شده به ایمیل خود را وارد کنید.'}
      size="sm"
      data-testid="verify-email-dialog"
      footer={
        <>
          <Button
            variant="primary"
            onClick={() => void submit(code)}
            loading={busy}
            leadingIcon={<MailCheck className="size-4" />}
            data-testid="verify-email-submit"
          >
            تایید
          </Button>
          <Button variant="ghost" onClick={closeVerifyDialog} disabled={busy} data-testid="verify-email-later">
            بعداً
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(code);
        }}
        className="flex flex-col gap-3.5"
      >
        {error && (
          <div role="alert" className="flex items-center gap-2 p-3 text-xs rounded-xl bg-error/15 border border-error/30 text-error-soft">
            <AlertCircle className="size-4 shrink-0" />
            <span data-testid="verify-email-error">{error}</span>
          </div>
        )}
        <Field label="کد تایید">
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            isLatin
            maxLength={CODE_LENGTH}
            placeholder="123456"
            value={code}
            onChange={(e) => {
              const next = normalizeCode(e.target.value);
              setCode(next);
              if (next.length === CODE_LENGTH) void submit(next);
            }}
            className="text-center tracking-[0.5em] text-lg"
            data-testid="verify-email-input"
          />
        </Field>
        <button
          type="button"
          className="self-start text-xs link link-primary"
          onClick={() => {
            closeVerifyDialog();
            openChangeEmailDialog();
          }}
          data-testid="verify-email-change"
        >
          ایمیل اشتباه است؟ تغییر ایمیل
        </button>
        <div className="flex items-center justify-between gap-3 text-xs text-base-content/70">
          <span>کد را دریافت نکردید؟ پوشه اسپم را هم نگاه کنید.</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void resend()}
            disabled={cooldown > 0 || busy}
            data-testid="verify-email-resend"
          >
            {cooldown > 0 ? `ارسال دوباره (${cooldown.toLocaleString('fa-IR')})` : 'ارسال دوباره'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
